// src/types.ts

// --- RISPOSTE API GENERICHE ---
export interface ApiResponse<T> {
    success: boolean;
    message: string;
    data: T;
}

// --- AUTH ---
export interface LoginRequest {
    email: string;
    password: string;
}

export interface AuthResponse {
    token?: string;
    id?: number;
    name?: string;
    email?: string;
    role?: string;
}

export interface PropertySubmission {
    id: number;
    createdAt: string;
    address: string;
    ownerName: string;
    unitType?: string;
    propertyType?: string;
    title?: string;
    summary?: string;
    wifiName?: string;
    wifiPassword?: string;
    // ... altri campi se necessario per il type checking stretto,
    // oppure usa un tipo parziale se i dati sono dinamici
    [key: string]: any;
}

export interface Review {
    id: string;
    authorName: string;
    content: string;
    date: string;
    rating: number;
    channel?: string;
}

export interface UserDto extends User {}

// --- PROPERTY ---
export interface Property {
    id: string;
    title: string;
    location: string;
    type: string;
    guests: number;
    beds: number;
    baths: number;
    price: number;
    rating: number;
    bedrooms: number;
    image: string;
    city: string;
    tag?: string;
    amenities?: string[];
    // MODIFICA QUI: description ora può essere una mappa chiave-valore o una stringa
    description?: Record<string, string> | string;
    images?: string[];
    totalPrice?: number;
    nights?: number;
    lat?: number;
    lng?: number;
    reviewsCount?: number;
}

// --- NAVIGAZIONE ---
export type Page = 'home' | 'villas' | 'rates' | 'location' | 'about' | 'contact' | 'faq' | 'admin' | 'compliance';

// --- DATI STATICI ---
export const faqs = [
    { q: "Come funziona il check-in smart?", a: "Riceverai un codice digitale per accedere alla proprietà in totale autonomia." },
    { q: "Offrite servizi di pulizia extra?", a: "Sì, il nostro team Hospitality Services può organizzare pulizie su richiesta." },
    { q: "C'è il Wi-Fi veloce?", a: "Tutte le nostre case Another Home sono dotate di fibra ottica ad alta velocità." },
    { q: "Politica di cancellazione?", a: "Flessibile fino a 5 giorni prima dell'arrivo per la maggior parte delle strutture." }
];

export interface ContactMessage {
    id: number;
    name: string;
    email: string;
    message: string;
    read: boolean;
    createdAt: string;
}

export interface FaqItem {
    id?: number;
    question: string;
    answer: string;
}

export interface SiteSettings {
    about_us_title?: string;
    about_us_text?: string;
    contact_phone?: string;
    contact_email?: string;
    [key: string]: string | undefined;
}

export interface QuoteResponse {
    listingId: string;
    checkIn: string;
    checkOut: string;
    guests: number;
    nights: number;
    available: boolean;
    currency: string;
    nightlyPrice: number;
    accommodationPrice: number;
    cleaningFee: number;
    serviceFee: number;
    discount?: number;
    taxes: number;
    totalPrice: number;
    message?: string;
}

export interface User {
    id: number;
    name: string;
    surname: string;
    email: string;
    role: string;
    token?: string;
}

export interface Testimonial {
    id?: number;
    name: string;
    role: string;
    property: string;
    preview: string;
    fullText: string;
    rating: number;
}

export type ManualBlockType = 'heading' | 'paragraph' | 'image' | 'video';

export interface ManualBlock {
    id: string;
    type: ManualBlockType;
    text?: string;
    url?: string;
}

export interface ManualItem {
    id?: number;
    title: string;
    instructions?: string;
    videoUrl?: string;
    content?: string;
    displayOrder?: number;
}

export interface HouseManual {
    id?: number;
    houseName: string;
    address: string;
    badge?: string;
    welcomeMessage?: string;
    assistancePhone?: string;
    checkinVideoUrl?: string;
    intercomCode?: string;
    keyBoxCode?: string;
    wifiName?: string;
    wifiPassword?: string;
    items: ManualItem[];
}

export interface HouseManualAdmin {
    id: number;
    houseName: string;
    address: string;
    badge?: string;
    guestSlug: string;
    editSlug: string;
    createdAt?: string;
    updatedAt?: string;
    itemsCount: number;
}

export interface BlogPost {
    id?: number;
    title: string;
    slug: string;
    excerpt: string;
    content: string;
    author: string;
    coverImageUrl: string;
    category: string;
    published: boolean;
    createdAt?: string;
    updatedAt?: string;
    certificationTxId?: string;
    certificationAccessKey?: string;
    certificationVerifyUrl?: string;
    certificationDate?: string;
}

// =====================================================================
// OWNER ONBOARDING
// Single source of truth for the Owner Onboarding data shapes.
// The backend stores propertySetupData / ownerFormData as JSON STRINGS.
// (ApiResponse<T> is already defined at the top of this file and reused.)
// =====================================================================

export type OnboardingStatus =
    | 'CREATED'
    | 'LINK_SENT'
    | 'OTP_VERIFIED'
    | 'PROPERTY_SETUP'
    | 'OWNER_FORM'
    | 'SIGNED'
    | 'CERTIFIED'
    | 'COMPLETED'
    | string;

// --- PUBLIC (no auth) ---
export interface OnboardingPublicInfo {
    ownerFirstName: string;
    propertyAddress: string;
    status: OnboardingStatus;
    maskedEmail: string;
}

export interface RequestOtpResult {
    maskedEmail: string;
}

export interface VerifyOtpResult {
    sessionToken: string;
    sessionExpiresAt: string; // ISO datetime
}

// --- SIGNATURE MODE / METHOD ---
export type SignatureMode = 'DRAW_OR_UPLOAD' | 'OTP';
export type SignatureMethod = 'DRAWN' | 'UPLOADED';

// --- IDENTITY DOCUMENTS ---
// An uploaded identity/bank document (no URLs are returned by the backend,
// only the metadata + content hash that is embedded in the signed certificate).
export interface IdentityDocument {
    filename: string;
    sha256: string;
    contentType: string;
    uploadedAt: string; // ISO datetime
}

// --- OWNER (X-Owner-Session) ---
// A signed document download link (presigned S3 URL).
export interface OnboardingDocumentLink {
    label: string;
    url: string;
}

// Raw payload as returned by GET /owner/{slug} (JSON string fields).
export interface OnboardingOwnerRaw {
    status: OnboardingStatus;
    ownerFirstName: string;
    ownerLastName: string;
    propertyAddress: string;
    propertySetupCompleted: boolean;
    ownerFormCompleted: boolean;
    agreementAccepted: boolean;
    signedAt?: string | null;
    certVerifyUrl?: string | null;
    certTxId?: string | null;
    finalPdfUrl?: string | null;
    // The 3 signed documents as presigned download links.
    documents?: OnboardingDocumentLink[] | null;
    signatureMode: SignatureMode;
    signatureMethod?: string | null;
    propertySetupData?: string | null; // JSON string -> PropertySetup
    ownerFormData?: string | null;     // JSON string -> OwnerForm
    availabilityData?: string | null;  // JSON string -> AvailabilityData (Exhibit A)
    // Identity/bank documents the owner has uploaded (metadata only, no URLs).
    identityDocuments?: IdentityDocument[] | null;
    // QR-stamped, blockchain-notarized Certificate of Authenticity (null until certified).
    certificateUrl?: string | null;
    // Note di modifica autorizzate, da leggere prima della firma.
    amendments?: AmendmentsMap | null;
}

// Parsed version used inside the app (propertySetupData / ownerFormData parsed).
export interface OnboardingOwnerData extends Omit<OnboardingOwnerRaw, 'propertySetupData' | 'ownerFormData'> {
    propertySetup: PropertySetup | null;
    ownerForm: OwnerForm | null;
}

// --- OWNER FORM (flat object, EXACT keys required by the backend) ---
export type ExecutionType = 'Individual' | 'Company';
export type BankAccountType = 'Checking' | 'Saving';

export interface OwnerForm {
    ownerFullName: string;
    dateOfBirth: string;
    contactPrimary: string;
    contactSecondary: string;
    emailAddress: string;
    propertyAddress: string;
    mailingAddress: string;
    executionType: ExecutionType;
    companyName: string;
    companyType: string;
    companyAddress: string;
    ssn: string;
    ein: string;
    bankAccountType: BankAccountType;
    bankAccountNumber: string;
    bankRoutingNumber: string;
    bankName: string;
    bankAddress: string;
}

// --- AVAILABILITY (Exhibit A del Co-hosting) ---
export type AvailabilityState = 'Open' | 'Blocked';

export interface AvailabilityMonth {
    month: AvailabilityState;
    twoweeks: AvailabilityState;
    week: AvailabilityState;
    weekend: AvailabilityState;
    midweek: AvailabilityState;
    minstay: string; // numero minimo di notti (stringa per input libero)
}

// Chiave = mese in minuscolo inglese (january..december).
export type AvailabilityData = Record<string, AvailabilityMonth>;

// --- PROPERTY SETUP (dynamic sections/items) ---
export type PropertySetupItemType = 'text' | 'checkbox';

export interface PropertySetupItem {
    label: string;
    value: string | boolean;
    type: PropertySetupItemType;
}

export interface PropertySetupSection {
    title: string;
    items: PropertySetupItem[];
}

// Flat map of docx template anchors -> value. Checkbox anchors hold a boolean,
// text anchors hold a string. This is what the backend `generatePropertySetupPdf`
// reads (propertySetupData.anchors) to fill the real Word template.
export type PropertySetupAnchors = Record<string, string | boolean>;

export interface PropertySetup {
    // Legacy structure consumed by the HTML preview path + admin panel.
    sections: PropertySetupSection[];
    // Flat anchor map consumed by the docx PDF generator (optional for backward compat).
    anchors?: PropertySetupAnchors;
}

// One entry of propertySetupSchema.json: an ordered docx anchor descriptor.
// `key` is the EXACT docx anchor (never remapped); `label` is the raw (often rough)
// text extracted from the template.
export interface PropertySetupSchemaEntry {
    section: string;
    key: string;
    type: PropertySetupItemType;
    label: string;
}

// Config used to render the Property Setup form (labels/types only, no values).
export interface PropertySetupItemConfig {
    label: string;
    type: PropertySetupItemType;
    /** Optional short helper text shown under the field. */
    hint?: string;
}

export interface PropertySetupSectionConfig {
    title: string;
    /** Optional intro paragraph rendered above the fields. */
    intro?: string;
    items: PropertySetupItemConfig[];
}

// --- SIGN ---
// DRAW_OR_UPLOAD mode: image + method + consent.
export interface SignRequestImage {
    signatureImageData: string; // data:image/png;base64,...
    signatureMethod: SignatureMethod;
    consentAccepted: boolean;
}

// OTP mode: code + consent.
export interface SignRequestOtp {
    code: string;
    consentAccepted: boolean;
}

export type SignRequest = SignRequestImage | SignRequestOtp;

export interface RequestSignOtpResult {
    maskedEmail: string;
}

// --- ADMIN ---
export interface CreateOnboardingRequest {
    ownerFirstName: string;
    ownerLastName: string;
    ownerEmail: string;
    propertyAddress: string;
    signatureMode?: SignatureMode;
}

// --- AMENDMENTS (note di modifica autorizzate, "Negotiated Modifications") ---
// I documenti che possono ricevere note. Allineati alle chiavi del backend.
export type AmendmentDocKey = 'property-setup' | 'owner-form' | 'cohosting';

export const AMENDMENT_DOC_LABELS: Record<AmendmentDocKey, string> = {
    'property-setup': 'Property Setup Guide',
    'owner-form': 'Owner Onboarding Form',
    cohosting: 'Co-Hosting Agreement',
};

export interface Amendment {
    reference?: string; // es. "Section 4.1 (Fee)"; vuoto = nota generale
    text: string;
    createdAt?: string;
    createdBy?: string;
}

// Mappa docKey -> lista note (le chiavi assenti significano "nessuna nota").
export type AmendmentsMap = Partial<Record<AmendmentDocKey, Amendment[]>>;

// Payload per la modifica anagrafica diretta da parte dell'admin (prima della firma).
export interface AdminProfileUpdate {
    ownerFirstName?: string;
    ownerLastName?: string;
    ownerEmail?: string;
    propertyAddress?: string;
}

// Payload per aggiungere una nota di modifica (admin).
export interface AddAmendmentRequest {
    document: AmendmentDocKey;
    reference?: string;
    text: string;
}

export interface AdminOnboarding {
    id: number;
    publicSlug: string;
    link: string;
    status: OnboardingStatus;
    ownerEmail: string;
    ownerFirstName?: string;
    ownerLastName?: string;
    propertyAddress?: string;
    propertySetupCompleted?: boolean;
    ownerFormCompleted?: boolean;
    agreementAccepted?: boolean;
    signedAt?: string | null;
    certVerifyUrl?: string | null;
    certTxId?: string | null;
    finalPdfUrl?: string | null;
    // I 3 documenti firmati come URL S3 firmati (TTL ~1h), disponibili dopo la firma/certificazione.
    documents?: OnboardingDocumentLink[] | null;
    // Certificate of Authenticity (QR + blockchain) come URL firmato.
    certificateUrl?: string | null;
    signatureMode?: SignatureMode;
    signatureMethod?: string | null;
    amendments?: AmendmentsMap;
    createdAt?: string;
}